import { Chip } from "@mui/material";
import React from "react";

// Shared formatting for the Security & Logs pages.

export const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

/** "2m 30s" / "45s" — used for rate-limit block TTLs. */
export const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return "-";
    const s = Number(seconds);
    if (!Number.isFinite(s) || s < 0) return "-";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
};

export const formatMs = (ms) => {
    if (ms === null || ms === undefined || ms === "") return "-";
    const n = Number(ms);
    if (!Number.isFinite(n)) return "-";
    return n >= 1000 ? `${(n / 1000).toFixed(2)}s` : `${n}ms`;
};

const statusColor = (code) => {
    if (code >= 500) return "error";
    if (code === 429) return "warning";
    if (code >= 400) return "warning";
    if (code >= 200 && code < 300) return "success";
    return "default";
};

export const StatusChip = ({ code }) => {
    if (code === null || code === undefined) return <span>-</span>;
    return (
        <Chip
            label={code}
            size="small"
            color={statusColor(Number(code))}
            variant="outlined"
            sx={{ fontWeight: 600, minWidth: 52 }}
        />
    );
};

/**
 * Combine a react-datepicker date with an "HH:mm" string into the
 * `YYYY-MM-DD HH:mm:ss` the API expects. `edge` decides the default time when
 * none was typed, so a date-only range still covers whole days.
 */
export const toApiDateTime = (date, time, edge) => {
    if (!date) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    if (!time) return `${datePart} ${edge === "start" ? "00:00:00" : "23:59:59"}`;
    return `${datePart} ${time}:${edge === "start" ? "00" : "59"}`;
};

/** Strip empty values so they are not sent as blank query params. */
export const compactParams = (obj) => {
    const out = {};
    Object.keys(obj).forEach((k) => {
        const v = obj[k];
        if (v !== undefined && v !== null && v !== "") out[k] = v;
    });
    return out;
};

/**
 * `customErrorNumber` from the response envelope. Only the codes that make a log
 * row readable on its own are named — anything else shows as the bare number.
 * Mirrors constants/errors.ts on the backend.
 */
export const ERROR_CODE_LABELS = {
    "-1000000": "Not Found — no such route",
    "-999999": "Invalid Request Format",
    100002: "Authorization Missing — no header sent",
    100003: "Authorization Invalid — token expired or revoked",
    100004: "OTP Expired",
    100005: "Invalid OTP",
    100035: "Session has been logged out",
    100036: "Bound to another device",
};

export const errorCodeLabel = (code) => {
    if (code === null || code === undefined || code === "") return "-";
    const label = ERROR_CODE_LABELS[String(code)];
    return label ? `${code} — ${label}` : String(code);
};

export const UNMATCHED_ROUTE = "__unmatched__";

/** Label for the "API wise" dropdown and the table's API column. */
export const routeLabel = (method, routePath) => {
    if (!routePath || routePath === UNMATCHED_ROUTE) return `${method || ""} (unmatched)`.trim();
    return `${method || ""} ${routePath}`.trim();
};

export const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
export const STATUS_GROUPS = [
    { value: "2xx", label: "2xx Success" },
    { value: "3xx", label: "3xx Redirect" },
    { value: "4xx", label: "4xx Client error" },
    { value: "5xx", label: "5xx Server error" },
];
export const SIDES = ["app", "admin", "brand", "company"];
export const USER_TYPES = ["influencer", "admin", "brand", "company"];
